import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import {
  IconArrowLeft,
  IconExternalLink,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { FormField } from '../panel/FormField';
import { SmartSelect, SearchAhead, DateField } from '../dropdown';
import type {
  InvoiceScheduleDetail,
  InvoiceScheduleLineItemDetail,
  InvoiceScheduleCreateRequest,
  InvoiceScheduleUpdateRequest,
  InvoiceScheduleCreateLineItem,
  CustomerIndexItemDto,
  CatalogProduct,
} from '@techgitter/contracts';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { getProducts } from '../../services/productCacheService';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import './InvoiceScheduleEditPanel.css';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

type Frequency = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

// RepairShopr recurring schedules support Weekly / Monthly / Yearly(Annually)
// — Daily is not an RS schedule frequency, so it's omitted from the picker.
const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Yearly' },
];

interface EditableLineItem {
  itemId?: string;
  /** Unique RepairShopr line id, round-tripped so the server matches this exact line on save. */
  repairShoprLineId?: number;
  /**
   * RepairShopr product id, captured from the catalog when a product is picked so the
   * created RS line is linked to the product (not just a free-text name). Cleared when the
   * SKU is typed by hand so a stale id is never sent.
   */
  productId?: number;
  name: string;
  description: string;
  quantity: number;
  priceRetail: number;
  oneTimeCharge: boolean;
}

interface InvoiceScheduleEditPanelProps {
  schedule: InvoiceScheduleDetail | null;
  isSaving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (payload: InvoiceScheduleCreateRequest | Omit<InvoiceScheduleUpdateRequest, 'scheduleId'>) => Promise<void>;
}

function emptyLineItem(): EditableLineItem {
  return { name: '', description: '', quantity: 1, priceRetail: 0, oneTimeCharge: false };
}

/** Coerce a product id (the API/catalog expose it as a string) to the numeric id RS expects. */
function parseProductId(value: string | number | undefined | null): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function detailToEditable(item: InvoiceScheduleLineItemDetail): EditableLineItem {
  return {
    itemId: item.itemId,
    repairShoprLineId: item.repairShoprLineId,
    productId: parseProductId(item.productId),
    name: item.name,
    description: item.description,
    quantity: item.quantity,
    priceRetail: item.priceRetail,
    oneTimeCharge: item.oneTimeCharge ?? false,
  };
}

function normalizeFrequency(value: string | undefined | null): Frequency {
  if (value === 'Annually') return 'Yearly';
  if (value === 'Daily' || value === 'Weekly' || value === 'Monthly' || value === 'Yearly') return value;
  return 'Monthly';
}

/**
 * Create / edit an invoice schedule — a full-page card-on-grey form (sm4rt-web-ui
 * §4): a recessive back link over floating `CARD_SX`-style cards, `FormField` +
 * native inputs at the comfortable tier, the unified `SmartSelect` / `DateField`
 * / `SearchAhead` controls, and the `DataTable` kit for the line-items grid.
 * Replaces the former white-`Paper` MUI form. Rendered inline by
 * `InvoiceScheduleAdminPage` in place of the list.
 */
export const InvoiceScheduleEditPanel: React.FC<InvoiceScheduleEditPanelProps> = ({
  schedule,
  isSaving,
  error,
  onCancel,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('Monthly');
  const [nextRun, setNextRun] = useState('');
  const [emailCustomer, setEmailCustomer] = useState(false);
  const [paused, setPaused] = useState(false);
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [products, setProducts] = useState<CatalogProduct[]>([]);

  const isCreate = schedule === null;

  useEffect(() => {
    getProducts().then((result) => setProducts(result.items.filter((p) => !p.disabled))).catch(() => {});
  }, []);

  useEffect(() => {
    if (schedule) {
      setName(schedule.name);
      setFrequency(normalizeFrequency(schedule.frequency));
      setNextRun(schedule.nextRun || '');
      setEmailCustomer(schedule.emailCustomer);
      setPaused(schedule.paused);
      setLineItems(schedule.lineItems.map(detailToEditable));
      setCustomer(null);
    } else {
      setName('');
      setFrequency('Monthly');
      setNextRun('');
      setEmailCustomer(false);
      setPaused(false);
      setLineItems([emptyLineItem()]);
      setCustomer(null);
    }
  }, [schedule]);

  // Deduped, sorted catalog backing the per-row SearchAhead product picker.
  const productList = useMemo(() => {
    const bySku = new Map<string, CatalogProduct>();
    for (const p of products) bySku.set(p.sku, p);
    return [...bySku.values()].sort((a, b) => a.sku.localeCompare(b.sku));
  }, [products]);

  const productBySku = useMemo(() => {
    const m = new Map<string, CatalogProduct>();
    for (const p of productList) m.set(p.sku, p);
    return m;
  }, [productList]);

  const productResultsFor = (query: string) => {
    const q = query.trim().toLowerCase();
    const base = q
      ? productList.filter(
          (p) => p.sku.toLowerCase().includes(q) || p.displayName.toLowerCase().includes(q),
        )
      : productList;
    return base.slice(0, 50).map((p) => ({ code: p.sku, description: p.displayName }));
  };

  const trimmedName = name.trim();

  // next-run is required on create: the orchestrator needs it to create the
  // RepairShopr schedule (RS rejects a schedule with no next run date).
  const isValid = isCreate
    ? trimmedName.length > 0 && customer !== null && nextRun.trim().length > 0
      && lineItems.length > 0 && lineItems.every((li) => li.name.trim().length > 0)
    : trimmedName.length > 0;

  const isDirty = isCreate || (
    trimmedName !== schedule.name ||
    frequency !== normalizeFrequency(schedule.frequency) ||
    nextRun !== (schedule.nextRun || '') ||
    emailCustomer !== schedule.emailCustomer ||
    paused !== schedule.paused ||
    JSON.stringify(lineItems) !== JSON.stringify(schedule.lineItems.map(detailToEditable))
  );

  const handleSave = async () => {
    if (!isDirty || !isValid) return;

    const lineItemPayload: InvoiceScheduleCreateLineItem[] = lineItems.map((li) => ({
      ...(li.itemId ? { itemId: li.itemId } : {}),
      ...(li.repairShoprLineId != null ? { repairShoprLineId: li.repairShoprLineId } : {}),
      ...(li.productId != null ? { productId: li.productId } : {}),
      name: li.name.trim(),
      description: li.description.trim(),
      quantity: li.quantity,
      priceRetail: li.priceRetail,
      priceCost: 0,
      taxable: true,
      oneTimeCharge: li.oneTimeCharge,
    }));

    if (isCreate) {
      const payload: InvoiceScheduleCreateRequest = {
        customer: {
          dataverseId: customer!.dataverseCustomerId,
          repairShoprId: customer!.repairShoprCustomerId,
        },
        name: trimmedName,
        frequency,
        nextRun: nextRun || undefined,
        emailCustomer,
        paused,
        lineItems: lineItemPayload,
      };
      await onSave(payload);
    } else {
      const payload: Omit<InvoiceScheduleUpdateRequest, 'scheduleId'> = {
        name: trimmedName,
        frequency,
        nextRun: nextRun || undefined,
        emailCustomer,
        paused,
        lineItems: lineItemPayload,
      };
      await onSave(payload);
    }
  };

  const handleAddLineItem = () => setLineItems([...lineItems, emptyLineItem()]);

  const handleRemoveLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index: number, field: keyof EditableLineItem, value: string | number | boolean) => {
    setLineItems(lineItems.map((li, i) => (i === index ? { ...li, [field]: value } : li)));
  };

  // Free-text typing in the product picker: update the name and drop any product id
  // captured from a previous pick so a hand-typed SKU is never sent with a stale id.
  const handleProductSearch = (index: number, text: string) => {
    setLineItems(lineItems.map((li, i) =>
      i === index ? { ...li, name: text, productId: undefined } : li,
    ));
  };

  const handleProductSelect = (index: number, product: CatalogProduct | null) => {
    if (!product) return;
    setLineItems(lineItems.map((li, i) =>
      i === index
        ? {
            ...li,
            name: product.sku,
            // Link the line to the RS product so the created line carries its product id.
            productId: parseProductId(product.productId),
            // Seed the description from the product only when the row has none —
            // never overwrite a custom description the user has already entered.
            description: li.description.trim() ? li.description : product.displayName,
            priceRetail: product.unitCost,
          }
        : li,
    ));
  };

  return (
    <div className="isch">
      <div className="isch__inner">
        {/* Back link (+ Open in RepairShopr when editing) */}
        <div className="isch__topbar">
          <button type="button" className="isch__back" onClick={onCancel} disabled={isSaving}>
            <IconArrowLeft size={16} stroke={1.5} />
            Back to schedules
          </button>
          <div className="isch__topbar-spacer" />
          {!isCreate && schedule?.repairShoprScheduleId && (
            <a
              className="isch__rs-link"
              href={`${REPAIRSHOPR_BASE_URL}/schedules/${schedule.repairShoprScheduleId}/edit`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in RepairShopr
              <IconExternalLink size={15} stroke={1.5} />
            </a>
          )}
        </div>

        {/* Details */}
        <div className="isch__card">
          <div className="isch__card-title">{isCreate ? 'Add Invoice Schedule' : 'Edit Invoice Schedule'}</div>

          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

          {isCreate && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5, fontSize: 13 }}>
              After creating, edit the schedule in RepairShopr to set:
              <strong> Last day of month</strong> (checked),
              <strong> Keep prices in sync</strong> (checked),
              <strong> Billing terms</strong> (Standard),
              <strong> Period mode</strong> (In advance inclusive).
            </Alert>
          )}

          {isCreate ? (
            <CustomerLookupControl value={customer} onChange={setCustomer} autoFocus />
          ) : (
            <FormField label="Customer" disabled>
              <input value={schedule?.customerName ?? ''} disabled />
            </FormField>
          )}

          <FormField label="Schedule name *">
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>

          <div className="isch__row">
            <div className="isch__select">
              <SmartSelect
                label="Frequency"
                value={frequency}
                options={FREQUENCY_OPTIONS}
                onChange={(v) => v && setFrequency(v as Frequency)}
              />
            </div>
            <DateField
              label={isCreate ? 'Next run *' : 'Next run'}
              value={nextRun || null}
              onChange={(iso) => setNextRun(iso ?? '')}
            />
          </div>

          <div className="isch__toggles">
            <label className="isch__check">
              <input type="checkbox" checked={emailCustomer} onChange={(e) => setEmailCustomer(e.target.checked)} />
              Email customer
            </label>
            <label className="isch__check">
              <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
              Paused
            </label>
          </div>
        </div>

        {/* Line items */}
        <div className="isch__card isch__card--flush">
          <div className="isch__card-head">
            <span className="isch__card-title isch__card-title--flush">Line items</span>
            <button type="button" className="isch__add-item" onClick={handleAddLineItem}>
              <IconPlus size={15} stroke={1.5} />
              Add item
            </button>
          </div>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell width="24%">Name</DataTableHeadCell>
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell width={84} align="right">Qty</DataTableHeadCell>
                <DataTableHeadCell width={104} align="right">Price $</DataTableHeadCell>
                <DataTableHeadCell width={78} align="center">One-time</DataTableHeadCell>
                <DataTableHeadCell width={44} />
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <DataTableEmpty colSpan={6}>No line items</DataTableEmpty>
              ) : (
                lineItems.map((li, index) => (
                  <DataTableRow key={index}>
                    <DataTableCell>
                      <div className="isch__li-product">
                        <SearchAhead
                          label=""
                          placeholder="Product"
                          value={li.name}
                          results={productResultsFor(li.name)}
                          onSearch={(text) => handleProductSearch(index, text)}
                          onSelect={(r) => {
                            const product = productBySku.get(r.code) ?? null;
                            handleProductSelect(index, product);
                          }}
                        />
                      </div>
                    </DataTableCell>
                    <DataTableCell>
                      <input
                        className="isch__li-input"
                        value={li.description}
                        placeholder="Description"
                        title={li.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <input
                        className="isch__li-input isch__li-input--num"
                        type="number"
                        min={0}
                        step={1}
                        value={li.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', Number(e.target.value) || 0)}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <input
                        className="isch__li-input isch__li-input--num"
                        type="number"
                        min={0}
                        step={0.01}
                        value={li.priceRetail}
                        onChange={(e) => handleLineItemChange(index, 'priceRetail', Number(e.target.value) || 0)}
                      />
                    </DataTableCell>
                    <DataTableCell align="center">
                      <input
                        type="checkbox"
                        className="isch__li-check"
                        checked={li.oneTimeCharge}
                        title="One-time charge — appears on the next invoice only, then does not recur"
                        onChange={(e) => handleLineItemChange(index, 'oneTimeCharge', e.target.checked)}
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <button
                        type="button"
                        className="isch__icon-btn"
                        title="Remove line item"
                        onClick={() => handleRemoveLineItem(index)}
                      >
                        <IconTrash size={16} stroke={1.5} />
                      </button>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </div>

        {/* Actions */}
        <div className="isch__actions">
          <button type="button" className="isch__cancel" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="isch__primary"
            onClick={handleSave}
            disabled={!isDirty || !isValid || isSaving}
          >
            {isSaving ? 'Saving…' : isCreate ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
